import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faPlay, faSave, faDownload, faUpload, faCode, faPalette,
  faStore, faRocket, faChartLine, faShoppingCart, faCreditCard,
  faStar, faEye, faEdit, faTrash, faCopy, faSpinner, faTimes,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { UnitEconomicsTemplate } from '../types/templateDesigner';
import { VisualTemplateDesigner } from './VisualTemplateDesigner';
import { VisualTemplateDesignerV2 } from './VisualTemplateDesignerV2';
import templateService from '../services/templateService';

interface TemplateDesignerTabProps {
  data?: any;
  loading?: boolean;
}

export const TemplateDesignerTab: React.FC<TemplateDesignerTabProps> = ({ data, loading }) => {
  const [view, setView] = useState<'marketplace' | 'json-editor' | 'visual-designer' | 'my-templates'>('marketplace');
  const [templates, setTemplates] = useState<UnitEconomicsTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<UnitEconomicsTemplate | null>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Fetch templates when "My Templates" view is opened
  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const fetchedTemplates = await templateService.getTemplates();
      setTemplates(fetchedTemplates);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Fetch templates on mount and when view changes to my-templates
  useEffect(() => {
    if (view === 'my-templates') {
      fetchTemplates();
    }
  }, [view]);

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded p-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xs font-semibold text-gray-900 mb-1 flex items-center">
              <FontAwesomeIcon icon={faPalette} className="mr-2 text-purple-600" />
              Template Designer
            </h2>
            <p className="text-xs text-gray-600">
              Create custom unit economics dashboards with drag-and-drop components, formulas, and pre-built templates
            </p>
          </div>
        </div>
      </div>

      {/* View Selector */}
      <div className="bg-white border border-gray-300 rounded p-2">
        <div className="flex space-x-2">
          <button
            onClick={() => setView('marketplace')}
            className={`px-3 py-2 text-xs font-medium rounded transition-colors ${
              view === 'marketplace'
                ? 'bg-purple-100 text-purple-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <FontAwesomeIcon icon={faStore} className="mr-2" />
            Template Marketplace
          </button>
          <button
            onClick={() => setView('my-templates')}
            className={`px-3 py-2 text-xs font-medium rounded transition-colors ${
              view === 'my-templates'
                ? 'bg-purple-100 text-purple-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <FontAwesomeIcon icon={faChartLine} className="mr-2" />
            My Templates
          </button>
          <button
            onClick={() => setView('json-editor')}
            className={`px-3 py-2 text-xs font-medium rounded transition-colors ${
              view === 'json-editor'
                ? 'bg-purple-100 text-purple-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <FontAwesomeIcon icon={faCode} className="mr-2" />
            JSON Editor
          </button>
          <button
            onClick={() => setView('visual-designer')}
            className={`px-3 py-2 text-xs font-medium rounded transition-colors ${
              view === 'visual-designer'
                ? 'bg-purple-100 text-purple-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <FontAwesomeIcon icon={faPalette} className="mr-2" />
            Visual Designer
          </button>
        </div>
      </div>

      {/* Content */}
      {view === 'marketplace' && <TemplateMarketplace onImport={(template) => setSelectedTemplate(template)} />}
      {view === 'my-templates' && (
        <MyTemplates
          templates={templates}
          loading={loadingTemplates}
          onNavigate={setView}
          onRefresh={fetchTemplates}
        />
      )}
      {view === 'json-editor' && <JSONTemplateEditor />}
      {view === 'visual-designer' && <VisualDesigner onTemplateCreated={fetchTemplates} />}
    </div>
  );
};

// ============================================================================
// TEMPLATE MARKETPLACE
// ============================================================================

interface TemplateMarketplaceProps {
  onImport: (template: any) => void;
}

const TemplateMarketplace: React.FC<TemplateMarketplaceProps> = ({ onImport }) => {
  const [category, setCategory] = useState<'all' | 'saas' | 'ecommerce' | 'fintech' | 'marketplace' | 'infrastructure'>('all');

  const marketplaceTemplates = [
    {
      id: 'saas_core',
      name: 'SaaS Core Unit Economics',
      description: 'Essential metrics for SaaS businesses: Cost per Customer, LTV/CAC, Revenue Efficiency',
      category: 'saas',
      icon: faRocket,
      downloads: 2345,
      rating: 4.8,
      reviews: 127,
      metrics: ['Cost per Customer', 'Revenue per $', 'LTV/CAC', 'Gross Margin %'],
      badge: 'Popular'
    },
    {
      id: 'ecommerce_core',
      name: 'E-commerce Unit Economics',
      description: 'Track order economics: Cost per Order, AOV, Fulfillment Efficiency',
      category: 'ecommerce',
      icon: faShoppingCart,
      downloads: 1823,
      rating: 4.9,
      reviews: 89,
      metrics: ['Cost per Order', 'AOV', 'Gross Margin per Order', 'Fulfillment %'],
      badge: 'Top Rated'
    },
    {
      id: 'fintech_txn',
      name: 'FinTech Transaction Economics',
      description: 'Transaction-based metrics: Cost per Transaction, Processing Efficiency',
      category: 'fintech',
      icon: faCreditCard,
      downloads: 987,
      rating: 4.7,
      reviews: 56,
      metrics: ['Cost per Txn', 'Processing Cost %', 'Avg Txn Size', 'Margin Analysis'],
      badge: 'New'
    }
  ];

  const filteredTemplates = category === 'all'
    ? marketplaceTemplates
    : marketplaceTemplates.filter(t => t.category === category);

  return (
    <div className="space-y-3">
      {/* Category Filter */}
      <div className="bg-white border border-gray-300 rounded p-3">
        <div className="flex items-center space-x-2">
          <span className="text-xs font-medium text-gray-700">Category:</span>
          {['all', 'saas', 'ecommerce', 'fintech'].map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat as any)}
              className={`px-3 py-1 text-xs rounded transition-colors ${
                category === cat
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Template Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filteredTemplates.map((template) => (
          <div key={template.id} className="bg-white border border-gray-300 rounded p-4 hover:shadow-lg transition-shadow">
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-purple-100 rounded flex items-center justify-center">
                  <FontAwesomeIcon icon={template.icon} className="text-purple-600" />
                </div>
                <div>
                  <h3 className="text-xs font-semibold text-gray-900">{template.name}</h3>
                  <div className="flex items-center space-x-2 mt-1">
                    <div className="flex items-center space-x-1">
                      <FontAwesomeIcon icon={faStar} className="text-yellow-500 text-xs" />
                      <span className="text-xs text-gray-600">{template.rating}</span>
                      <span className="text-xs text-gray-500">({template.reviews})</span>
                    </div>
                  </div>
                </div>
              </div>
              {template.badge && (
                <span className={`px-2 py-1 text-xs rounded ${
                  template.badge === 'Popular' ? 'bg-blue-100 text-blue-700' :
                  template.badge === 'Top Rated' ? 'bg-green-100 text-green-700' :
                  'bg-purple-100 text-purple-700'
                }`}>
                  {template.badge}
                </span>
              )}
            </div>

            {/* Description */}
            <p className="text-xs text-gray-600 mb-3">{template.description}</p>

            {/* Metrics */}
            <div className="mb-3">
              <div className="text-xs text-gray-500 mb-1">Included Metrics:</div>
              <div className="flex flex-wrap gap-1">
                {template.metrics.map((metric, idx) => (
                  <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                    {metric}
                  </span>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div className="text-xs text-gray-500 mb-3">
              {template.downloads.toLocaleString()} downloads
            </div>

            {/* Actions */}
            <div className="flex space-x-2">
              <button className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 text-xs font-medium rounded hover:bg-gray-200 transition-colors">
                <FontAwesomeIcon icon={faEye} className="mr-1" />
                Preview
              </button>
              <button
                onClick={() => onImport(template)}
                className="flex-1 px-3 py-2 bg-purple-600 text-white text-xs font-medium rounded hover:bg-purple-700 transition-colors"
              >
                <FontAwesomeIcon icon={faDownload} className="mr-1" />
                Import
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredTemplates.length === 0 && (
        <div className="bg-white border border-gray-300 rounded p-12 text-center">
          <FontAwesomeIcon icon={faStore} className="text-6xl text-gray-300 mb-4" />
          <h3 className="text-xs font-semibold text-gray-900 mb-2">No Templates Found</h3>
          <p className="text-xs text-gray-600">Try selecting a different category</p>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MY TEMPLATES
// ============================================================================

interface MyTemplatesProps {
  templates: UnitEconomicsTemplate[];
  loading: boolean;
  onNavigate: (view: 'marketplace' | 'json-editor' | 'visual-designer' | 'my-templates') => void;
  onRefresh: () => void;
}

const MyTemplates: React.FC<MyTemplatesProps> = ({ templates, loading, onNavigate, onRefresh }) => {
  const [deleting, setDeleting] = useState<string | null>(null);

  const handleCreateTemplate = () => {
    // Navigate to Visual Designer to create new template
    onNavigate('visual-designer');
  };

  const handleBrowseMarketplace = () => {
    // Navigate to Marketplace
    onNavigate('marketplace');
  };

  const handleDeleteTemplate = async (templateId: string, templateName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${templateName}"?`)) {
      return;
    }

    setDeleting(templateId);
    try {
      await templateService.deleteTemplate(templateId);
      alert(`✅ Template "${templateName}" deleted successfully!`);
      onRefresh(); // Refresh the list
    } catch (error: any) {
      console.error('Error deleting template:', error);
      alert(`❌ Failed to delete template: ${error.message}`);
    } finally {
      setDeleting(null);
    }
  };

  const handleCloneTemplate = async (templateId: string, templateName: string) => {
    try {
      await templateService.cloneTemplate(templateId, `${templateName} (Copy)`);
      alert(`✅ Template cloned successfully!`);
      onRefresh(); // Refresh the list
    } catch (error: any) {
      console.error('Error cloning template:', error);
      alert(`❌ Failed to clone template: ${error.message}`);
    }
  };

  const getMetricIcon = (type: string) => {
    switch (type) {
      case 'saas': return faRocket;
      case 'ecommerce': return faShoppingCart;
      case 'fintech': return faCreditCard;
      default: return faChartLine;
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button
          onClick={handleCreateTemplate}
          className="px-4 py-2 bg-purple-600 text-white text-xs font-medium rounded hover:bg-purple-700"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          New Template
        </button>
      </div>

      {loading ? (
        <div className="bg-white border border-gray-300 rounded p-12 text-center">
          <FontAwesomeIcon icon={faSpinner} spin className="text-6xl text-purple-600 mb-4" />
          <h3 className="text-xs font-semibold text-gray-900 mb-2">Loading Templates...</h3>
        </div>
      ) : templates.length === 0 ? (
        <div className="bg-white border border-gray-300 rounded p-12 text-center">
          <FontAwesomeIcon icon={faChartLine} className="text-6xl text-gray-300 mb-4" />
          <h3 className="text-xs font-semibold text-gray-900 mb-2">No Templates Yet</h3>
          <p className="text-xs text-gray-600 mb-6">
            Create a new template or import one from the marketplace
          </p>
          <div className="flex justify-center space-x-3">
            <button
              onClick={handleCreateTemplate}
              className="px-6 py-3 bg-purple-600 text-white text-xs font-medium rounded hover:bg-purple-700"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Create Template
            </button>
            <button
              onClick={handleBrowseMarketplace}
              className="px-6 py-3 bg-gray-100 text-gray-700 text-xs font-medium rounded hover:bg-gray-200"
            >
              <FontAwesomeIcon icon={faStore} className="mr-2" />
              Browse Marketplace
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {templates.map((template) => (
            <div key={template.id} className="bg-white border border-gray-300 rounded p-4 hover:shadow-lg transition-shadow">
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 bg-purple-100 rounded flex items-center justify-center">
                    <FontAwesomeIcon icon={getMetricIcon(template.category)} className="text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-xs font-semibold text-gray-900">{template.name}</h3>
                    <div className="text-xs text-gray-500 capitalize">{template.category}</div>
                  </div>
                </div>
              </div>

              {/* Description */}
              <p className="text-xs text-gray-600 mb-3">{template.description || 'Custom template'}</p>

              {/* Components Count */}
              <div className="mb-3">
                <div className="text-xs text-gray-500">
                  {template.components?.length || 0} components
                </div>
              </div>

              {/* Last Used */}
              {template.lastUsed && (
                <div className="text-xs text-gray-500 mb-3">
                  Last used: {new Date(template.lastUsed).toLocaleDateString()}
                </div>
              )}

              {/* Actions */}
              <div className="flex space-x-2">
                <button
                  onClick={() => handleCloneTemplate(template.id, template.name)}
                  className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 text-xs font-medium rounded hover:bg-gray-200 transition-colors"
                  title="Clone template"
                >
                  <FontAwesomeIcon icon={faCopy} className="mr-1" />
                  Clone
                </button>
                <button
                  onClick={() => handleDeleteTemplate(template.id, template.name)}
                  disabled={deleting === template.id}
                  className="px-3 py-2 bg-red-100 text-red-700 text-xs font-medium rounded hover:bg-red-200 transition-colors disabled:opacity-50"
                  title="Delete template"
                >
                  {deleting === template.id ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <FontAwesomeIcon icon={faTrash} />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// JSON TEMPLATE EDITOR
// ============================================================================

const JSONTemplateEditor: React.FC = () => {
  const [jsonInput, setJsonInput] = useState('');
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const sampleTemplate = `{
  "name": "My Custom Template",
  "category": "custom",
  "components": [
    {
      "id": "customers",
      "type": "input_metric",
      "name": "Customer Count",
      "config": {
        "metricType": "customer_count",
        "dataType": "number",
        "unit": "customers"
      }
    },
    {
      "id": "costs",
      "type": "cost_source",
      "name": "Total Costs",
      "config": {
        "costType": "combined"
      }
    },
    {
      "id": "cost_per_customer",
      "type": "calculated_metric",
      "name": "Cost per Customer",
      "config": {
        "formula": {
          "type": "operator",
          "operator": "divide",
          "operands": [
            { "type": "variable", "variableId": "costs" },
            { "type": "variable", "variableId": "customers" }
          ]
        },
        "dataType": "currency"
      }
    }
  ]
}`;

  const handleExecute = async () => {
    setExecuting(true);
    setError(null);

    try {
      const template = JSON.parse(jsonInput);
      // Call API to execute template
      setResult({
        status: 'success',
        message: 'Template executed successfully!',
        metrics: [
          { name: 'Cost per Customer', value: '$4.50', trend: 'down' }
        ]
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded p-3">
        <h3 className="text-xs font-semibold text-blue-900 mb-2">How to use JSON Editor</h3>
        <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
          <li>Paste or write your template JSON below</li>
          <li>Components must have: id, type, name, and config</li>
          <li>Use formulas to calculate metrics dynamically</li>
          <li>Click "Execute Template" to see results</li>
        </ul>
      </div>

      {/* Editor */}
      <div className="bg-white border border-gray-300 rounded p-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold text-gray-900">Template JSON</h3>
          <div className="flex space-x-2">
            <button
              onClick={() => setJsonInput(sampleTemplate)}
              className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            >
              Load Sample
            </button>
            <button className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
              <FontAwesomeIcon icon={faSave} className="mr-1" />
              Save
            </button>
          </div>
        </div>

        <textarea
          value={jsonInput}
          onChange={(e) => setJsonInput(e.target.value)}
          className="w-full h-96 px-3 py-2 border border-gray-300 rounded font-mono text-xs"
          placeholder="Paste your template JSON here..."
        />

        {error && (
          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
            <strong>Error:</strong> {error}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-2">
        <button className="px-4 py-2 bg-gray-100 text-gray-700 text-xs font-medium rounded hover:bg-gray-200">
          <FontAwesomeIcon icon={faSave} className="mr-2" />
          Save Template
        </button>
        <button
          onClick={handleExecute}
          disabled={executing || !jsonInput}
          className="px-4 py-2 bg-purple-600 text-white text-xs font-medium rounded hover:bg-purple-700 disabled:opacity-50"
        >
          {executing ? (
            <>
              <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
              Executing...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faPlay} className="mr-2" />
              Execute Template
            </>
          )}
        </button>
      </div>

      {/* Results */}
      {result && (
        <div className="bg-green-50 border border-green-200 rounded p-4">
          <div className="flex items-center space-x-2 mb-3">
            <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
            <h3 className="text-xs font-semibold text-green-900">{result.message}</h3>
          </div>
          <div className="space-y-2">
            {result.metrics?.map((metric: any, idx: number) => (
              <div key={idx} className="bg-white rounded p-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-700">{metric.name}</span>
                  <span className="text-xs font-bold text-gray-900">{metric.value}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// VISUAL DESIGNER
// ============================================================================

interface VisualDesignerProps {
  onTemplateCreated?: () => void;
}

const VisualDesigner: React.FC<VisualDesignerProps> = ({ onTemplateCreated }) => {
  const [saving, setSaving] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleSave = async (template: UnitEconomicsTemplate) => {
    setSaving(true);
    setSaveSuccess(false);

    try {
      // Remove id if it's a new template
      const { id, createdAt, updatedAt, ...templateData } = template;

      let savedTemplate;
      if (id && id.startsWith('template_')) {
        // New template (id generated client-side)
        savedTemplate = await templateService.createTemplate(templateData as any);
      } else if (id) {
        // Existing template - update it
        savedTemplate = await templateService.updateTemplate(id, templateData);
      } else {
        // No id - create new
        savedTemplate = await templateService.createTemplate(templateData as any);
      }

      console.log('Template saved successfully:', savedTemplate);
      setSaveSuccess(true);

      // Show success message
      alert(`✅ Template "${savedTemplate.name}" saved successfully!`);

      // Notify parent to refresh templates list
      onTemplateCreated?.();

      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error: any) {
      console.error('Error saving template:', error);
      alert(`❌ Failed to save template: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleExecute = async (_template: UnitEconomicsTemplate) => {
    // The visual designer now runs execute-draft directly on the backend and
    // owns the results panel — no extra round trip needed here. Keep this as a
    // hook for analytics / global side-effects later.
    setExecuting(true);
    try {
      // intentionally no-op
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div className="p-4">
      {saveSuccess && (
        <div className="bg-green-100 border border-green-400 rounded p-3 mb-3">
          <p className="text-sm font-bold text-green-800">✅ Template saved successfully!</p>
        </div>
      )}
      {saving && (
        <div className="bg-blue-100 border border-blue-400 rounded p-3 mb-3">
          <p className="text-sm font-bold text-blue-800">
            <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
            Saving template...
          </p>
        </div>
      )}
      {executing && (
        <div className="bg-purple-100 border border-purple-400 rounded p-3 mb-3">
          <p className="text-sm font-bold text-purple-800">
            <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
            Executing template...
          </p>
        </div>
      )}
      <VisualTemplateDesignerV2 onSave={handleSave} onExecute={handleExecute} />
    </div>
  );
};

export default TemplateDesignerTab;
